'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { CatalogItem, StorageAllocation, StorageAllocationInput } from '@garage/shared'
import { storageAllocationInputSchema } from '@garage/shared'
import { Dialog, TextField, Autocomplete, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface AllocationFormDialogProps {
  open: boolean
  levelId: string
  /** Editing this allocation; undefined means create. */
  allocation?: StorageAllocation
  onClose: () => void
}

interface FormState {
  itemName: string
  catalogItemId: string | null
  quantityStr: string
  notes: string | null
}

/**
 * Catalog-assisted, not catalog-required: typing searches existing parts AND
 * materials (liquids like oil are usually tracked as 'material', not 'part'),
 * but picking a suggestion is optional — most things on a shelf, especially
 * consumables, were never added to the price-book Catalog and don't need to be.
 */
async function searchCatalog(query: string): Promise<CatalogItem[]> {
  const [parts, materials] = await Promise.all([
    api.catalog.suggest('part', query),
    api.catalog.suggest('material', query),
  ])
  return [...parts, ...materials]
}

export function AllocationFormDialog({ open, levelId, allocation, onClose }: AllocationFormDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const isCreate = !allocation

  const initialState = (): FormState => ({
    itemName: allocation?.itemName ?? '',
    catalogItemId: allocation?.catalogItemId ?? null,
    quantityStr: String(allocation?.quantity ?? 1),
    notes: allocation?.notes ?? null,
  })

  const [formState, setFormState] = useState<FormState>(initialState)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState(initialState())
      setFieldErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allocation])

  const mutation = useMutation({
    mutationFn: async (input: StorageAllocationInput) =>
      isCreate
        ? api.storage.createAllocation(levelId, input)
        : api.storage.updateAllocation(allocation!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.storageTree })
      snackbar.success(isCreate ? t('storage.itemAdded') : t('storage.itemUpdated'))
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const quantity = Number.parseInt(formState.quantityStr, 10)
    const input: StorageAllocationInput = {
      itemName: formState.itemName,
      catalogItemId: formState.catalogItemId,
      quantity: Number.isFinite(quantity) ? quantity : 0,
      notes: formState.notes,
    }

    const result = storageAllocationInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('storage.addItem') : t('storage.editItem')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Autocomplete
          label={t('storage.itemLabel')}
          value={formState.itemName}
          onChange={(text) =>
            setFormState((s) => ({ ...s, itemName: text, catalogItemId: text === s.itemName ? s.catalogItemId : null }))
          }
          onSelect={(item: CatalogItem) =>
            setFormState((s) => ({ ...s, itemName: item.name, catalogItemId: item.id }))
          }
          fetchOptions={searchCatalog}
          getOptionLabel={(item) => item.name}
          getOptionKey={(item) => item.id}
          renderOption={(item) => (
            <div>
              <div className="font-medium">{item.name}</div>
              {item.partNumber && <div className="text-xs text-on-surface-variant">{item.partNumber}</div>}
            </div>
          )}
          placeholder={t('storage.searchCatalogPlaceholder')}
          error={fieldErrors.itemName}
          supportingText={
            formState.catalogItemId ? t('storage.linkedToCatalog') : t('storage.notLinked')
          }
          autoFocus
        />

        <TextField
          label={t('storage.quantity')}
          value={formState.quantityStr}
          onChange={(v) => setFormState((s) => ({ ...s, quantityStr: v }))}
          error={fieldErrors.quantity}
          integer
          fullWidth
        />

        <TextField
          label={t('storage.notesOptional')}
          value={formState.notes ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, notes: v || null }))}
          error={fieldErrors.notes}
          multiline
          rows={2}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
